import { IProjectJson } from '@scale/shared/types/project';
import { SensitiveContentReportingConfig } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import apiFetch from 'react-scale/lib/api-fetch';
import { useState } from 'react';
import { useAsync } from 'react-use-v13';
import { isAnyGaiusCustomerId } from '@scale/shared/customers';

function useSensitiveContentReporting(
  project: string | IProjectJson,
  type: TaskType,
  taskCustomer: string | undefined,
  isReview: boolean = false,
): {
  enableSensitiveContentReporting: boolean;
  enableHighRiskSensitiveContentReporting: boolean;
  enableRegisterTaskAttemptForSensitiveContentReporting: boolean;
  useBulbaSensitiveContentReporting: boolean;
} {
  const projectId = (project as IProjectJson)._id ?? project;

  const isTaskTypeEnabledForSensitiveContent =
    type === TaskType.TextCollection ||
    type === TaskType.ImageAnnotation ||
    type === TaskType.SegmentAnnotation ||
    type === TaskType.Chat;

  const isTaskTypeEnabledForHighRiskSensitiveContent =
    type === TaskType.TextCollection ||
    type === TaskType.ImageAnnotation ||
    type === TaskType.SegmentAnnotation ||
    type === TaskType.Chat;

  const [enableSensitiveContentReportingConfig, setEnableSensitiveContentReportingConfig] =
    useState(false);
  const [
    enableHighRiskSensitiveContentReportingConfig,
    setEnableHighRiskSensitiveContentReportingConfig,
  ] = useState(false);
  const [
    enableRegisterTaskAttemptForSensitiveContentReportingConfig,
    setEnableRegisterTaskAttemptForSensitiveContentReportingConfig,
  ] = useState(false);

  useAsync(async () => {
    const {
      sensitiveContentReportingEnabledForAttempt,
      sensitiveContentReportingEnabledForReview,
      highRiskSensitiveContentReportingEnabledForAttempt,
      highRiskSensitiveContentReportingEnabledForReview,
      registerTaskAttemptsEnabledForSensitiveContentReporting,
    } = await apiFetch<SensitiveContentReportingConfig>(
      null,
      `internal/sensitive-content-reporting/config/${projectId}`,
      { method: 'GET' },
    );
    setEnableSensitiveContentReportingConfig(
      isReview
        ? sensitiveContentReportingEnabledForReview
        : sensitiveContentReportingEnabledForAttempt,
    );
    setEnableHighRiskSensitiveContentReportingConfig(
      isReview
        ? highRiskSensitiveContentReportingEnabledForReview
        : highRiskSensitiveContentReportingEnabledForAttempt,
    );
    setEnableRegisterTaskAttemptForSensitiveContentReportingConfig(
      registerTaskAttemptsEnabledForSensitiveContentReporting,
    );
  });

  const enableSensitiveContentReporting =
    enableSensitiveContentReportingConfig && isTaskTypeEnabledForSensitiveContent;

  const enableHighRiskSensitiveContentReporting =
    enableHighRiskSensitiveContentReportingConfig && isTaskTypeEnabledForHighRiskSensitiveContent;

  const enableRegisterTaskAttemptForSensitiveContentReporting =
    enableRegisterTaskAttemptForSensitiveContentReportingConfig;

  const useBulbaSensitiveContentReporting = isAnyGaiusCustomerId(taskCustomer!);

  return {
    enableSensitiveContentReporting,
    enableHighRiskSensitiveContentReporting,
    enableRegisterTaskAttemptForSensitiveContentReporting,
    useBulbaSensitiveContentReporting,
  };
}

export default useSensitiveContentReporting;
